import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixIntakeformsV1IntakeFormsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/intake-forms/v1/intake-forms',
        destPath: '/v1/intake-forms',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/intake-forms',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/intake-forms',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/intake-forms',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/intake-forms/v1/intake-forms',
        destPath: '/v1/intake-forms',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/intake-forms/v1/intake-forms',
        destPath: '/v1/intake-forms',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/intake-forms/v1/intake-forms',
        destPath: '/v1/intake-forms',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/intake-forms/v1/intake-forms',
        destPath: '/v1/intake-forms',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/intake-forms/v1/intake-forms',
        destPath: '/v1/intake-forms',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/intake-forms/v1/intake-forms',
        destPath: '/v1/intake-forms',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_intake-forms_intake-forms';

/**
 * Retrieves a list of intake forms, with the specified paging, filtering, and sorting.
 *
 * Query Intake Forms runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 *
 * Learn more about [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
 */
export function queryIntakeForms(payload: object): RequestOptionsFactory<any> {
  function __queryIntakeForms({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.intake_forms.v1.intake_form',
      method: 'POST' as any,
      methodFqn: 'wix.intakeforms.v1.IntakeFormsService.QueryIntakeForms',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIntakeformsV1IntakeFormsServiceUrl({
        protoPath: '/v1/intake-forms/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'intakeForms.createdDate' },
              { path: 'intakeForms.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryIntakeForms;
}

/**
 * Updates the expiration period of a specific intake form.
 *
 * When you update the expiration period, the system recalculates expiration for all existing submissions.
 */
export function updateIntakeFormExpirationPeriod(
  payload: object
): RequestOptionsFactory<any> {
  function __updateIntakeFormExpirationPeriod({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.intake_forms.v1.intake_form',
      method: 'PATCH' as any,
      methodFqn:
        'wix.intakeforms.v1.IntakeFormsService.UpdateIntakeFormExpirationPeriod',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIntakeformsV1IntakeFormsServiceUrl({
        protoPath: '/v1/intake-forms/{intakeFormId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'intakeForm.createdDate' },
              { path: 'intakeForm.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateIntakeFormExpirationPeriod;
}

/**
 * Creates a shareable link for customers to submit an intake form.
 *
 * The link is valid for 72 hours.
 */
export function createCustomerSubmissionLink(
  payload: object
): RequestOptionsFactory<any> {
  function __createCustomerSubmissionLink({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.intake_forms.v1.intake_form',
      method: 'GET' as any,
      methodFqn:
        'wix.intakeforms.v1.IntakeFormsService.CreateCustomerSubmissionLink',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIntakeformsV1IntakeFormsServiceUrl({
        protoPath: '/v1/intake-forms/{intakeFormId}/link',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __createCustomerSubmissionLink;
}

/**
 * Deletes an intake form.
 *
 *
 * Also deletes the underlying [Wix form](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/introduction).
 *
 * Existing [submissions](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-form-submissions/introduction) for the intake form aren't automatically deleted but become orphaned.
 * When [querying submissions](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-form-submissions/query-intake-form-submissions), orphaned submissions aren't returned.
 */
export function deleteIntakeForm(payload: object): RequestOptionsFactory<any> {
  function __deleteIntakeForm({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.intake_forms.v1.intake_form',
      method: 'DELETE' as any,
      methodFqn: 'wix.intakeforms.v1.IntakeFormsService.DeleteIntakeForm',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIntakeformsV1IntakeFormsServiceUrl({
        protoPath: '/v1/intake-forms/{intakeFormId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteIntakeForm;
}

/**
 * Archives an intake form, disabling it from accepting new submissions.
 *
 * Existing submissions remain accessible. You can [unarchive](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-forms/unarchive-intake-form) the form later to resume accepting submissions.
 */
export function archiveIntakeForm(payload: object): RequestOptionsFactory<any> {
  function __archiveIntakeForm({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.intake_forms.v1.intake_form',
      method: 'POST' as any,
      methodFqn: 'wix.intakeforms.v1.IntakeFormsService.ArchiveIntakeForm',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIntakeformsV1IntakeFormsServiceUrl({
        protoPath: '/v1/intake-forms/{intakeFormId}/archive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'intakeForm.createdDate' },
              { path: 'intakeForm.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __archiveIntakeForm;
}

/** Unarchives an intake form, enabling it to accept new submissions. */
export function unarchiveIntakeForm(
  payload: object
): RequestOptionsFactory<any> {
  function __unarchiveIntakeForm({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.intake_forms.v1.intake_form',
      method: 'POST' as any,
      methodFqn: 'wix.intakeforms.v1.IntakeFormsService.UnarchiveIntakeForm',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIntakeformsV1IntakeFormsServiceUrl({
        protoPath: '/v1/intake-forms/{intakeFormId}/unarchive',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'intakeForm.createdDate' },
              { path: 'intakeForm.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __unarchiveIntakeForm;
}
